import { useState, useEffect } from 'react'
import { User, Save, Loader2, AlertCircle, CheckCircle, Pencil } from 'lucide-react'
import { api } from '@/lib/api'

interface ProfileData {
  first_name: string; middle_name: string; last_name: string; display_name: string
  job_title: string; department: string; phone: string; timezone: string; locale: string
  avatar_url: string | null; profile_completed_at: string | null
  created_at: string | null; updated_at: string | null
}

interface UserData {
  user_id: string; email: string; display_name: string; role: string; status: string
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="min-w-0 py-3.5">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.13em] text-text-muted">{label}</span>
      <span className="block break-words text-[15px] leading-6 text-text-primary [overflow-wrap:anywhere]">{value || '—'}</span>
    </div>
  )
}

function Avatar({ name, url, size = 64 }: { name: string; url?: string | null; size?: number }) {
  if (url) return <img src={url} alt={name} className="rounded-full object-cover" style={{ width: size, height: size }} />
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  return <div className="rounded-full bg-accent/15 flex items-center justify-center text-accent font-semibold" style={{ width: size, height: size, fontSize: size * 0.35 }}>{initials}</div>
}

export function MyProfilePanel() {
  const [user, setUser] = useState<UserData | null>(null)
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  async function loadProfile() {
    setLoading(true); setError('')
    try {
      const data = await api.getMyProfile()
      if (data.user) setUser(data.user as unknown as UserData)
      if (data.profile) setProfile(data.profile as unknown as ProfileData)
    } catch (e) { setError('Failed to load profile: ' + (e instanceof Error ? e.message : '')) }
    setLoading(false)
  }

  useEffect(() => { loadProfile() }, [])

  function startEdit() {
    if (!profile) return
    setEditData({
      first_name: profile.first_name || '', last_name: profile.last_name || '',
      display_name: profile.display_name || '', job_title: profile.job_title || '',
      department: profile.department || '', phone: profile.phone || '',
      timezone: profile.timezone || 'Europe/London', locale: profile.locale || 'en-GB'
    })
    setEditing(true); setSuccess('')
  }

  async function saveProfile() {
    setSaving(true); setError(''); setSuccess('')
    try {
      const res = await api.updateMyProfile(editData)
      if (res.success) {
        setSuccess(res.profile_completed ? 'Profile completed — your account is now active.' : 'Profile updated successfully.')
        setEditing(false)
        await loadProfile()
      } else { setError('Save failed') }
    } catch { setError('Connection failed') }
    setSaving(false)
  }

  if (loading) {
    return (
      <div className="p-6 max-w-2xl flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
        <span className="text-sm text-text-muted">Loading profile...</span>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="p-6 max-w-2xl">
        <div className="flex items-center gap-2 p-3 bg-red-500/5 border border-red-500/10 rounded-lg text-sm text-red-400">
          <AlertCircle className="w-4 h-4" /> {error || 'Could not load profile. You may not be authenticated.'}
        </div>
      </div>
    )
  }

  const isFirstTime = profile && !profile.profile_completed_at

  return (
    <div className="relative min-h-full w-full overflow-hidden bg-[var(--workspace-page-bg)] pb-12">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[26rem] bg-[radial-gradient(circle_at_82%_12%,rgba(59,130,246,0.12),transparent_32%),radial-gradient(circle_at_8%_0%,rgba(14,165,233,0.07),transparent_28%)]" />

      <div className="relative px-5 pb-8 pt-8 sm:px-8 sm:pt-10 lg:px-12">
        <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent sm:text-xs">
          <User className="h-4 w-4" /> My Profile
        </div>
        <h1 className="mt-5 max-w-2xl font-serif text-[2.25rem] font-semibold leading-[1.05] tracking-[-0.03em] text-text-primary sm:text-5xl">
          Your profile and account details.
        </h1>
        <p className="mt-4 max-w-2xl text-[15px] leading-7 text-text-muted sm:text-lg">
          {isFirstTime
            ? 'Review your details, make any corrections and confirm your profile to activate your account.'
            : 'View and update the information associated with your OPS account.'}
        </p>

        {error && <div className="mt-5 flex items-start gap-2 rounded-2xl bg-red-500/6 px-4 py-3 text-sm text-red-500 ring-1 ring-red-500/10"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}</div>}
        {success && <div className="mt-5 flex items-start gap-2 rounded-2xl bg-emerald-500/6 px-4 py-3 text-sm text-emerald-600 ring-1 ring-emerald-500/10"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0" /> {success}</div>}

        {isFirstTime && (
          <div className="mt-5 rounded-2xl bg-amber-500/7 px-4 py-3 ring-1 ring-amber-500/15">
            <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">Profile confirmation required</div>
            <div className="mt-1 text-xs leading-5 text-text-muted">Your administrator entered your initial details. Review them below, make corrections if required, then save to activate your account.</div>
          </div>
        )}

        <section className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar name={user.display_name || user.email} url={profile?.avatar_url} size={72} />
            <div className="min-w-0 flex-1">
              <div className="text-2xl font-semibold leading-tight text-text-primary sm:text-3xl">{user.display_name || user.email.split('@')[0]}</div>
              <div className="mt-1 break-all text-sm leading-5 text-text-muted sm:text-base">{user.email}</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">{user.role}</span>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  user.status === 'active' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'
                }`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${user.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                  {user.status}
                </span>
              </div>
            </div>
          </div>
          {!editing && (
            <button onClick={startEdit} className="inline-flex min-h-11 items-center justify-center gap-2 self-start rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-text-secondary shadow-[0_10px_28px_rgba(15,23,42,0.055)] transition hover:-translate-y-0.5 hover:text-accent dark:bg-slate-900 sm:self-auto">
              <Pencil className="h-4 w-4" /> {isFirstTime ? 'Review & Confirm' : 'Edit Profile'}
            </button>
          )}
        </section>
      </div>

      <section className="w-full bg-white/95 px-5 py-6 shadow-[0_-1px_0_rgba(148,163,184,0.08),0_12px_32px_rgba(15,23,42,0.04)] dark:bg-slate-900/92 sm:mx-8 sm:w-[calc(100%-4rem)] sm:rounded-[1.75rem] sm:px-6 lg:mx-12 lg:w-[calc(100%-6rem)]">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">Profile Details</div>
            <h2 className="mt-1 text-lg font-semibold text-text-primary">Personal & work information</h2>
          </div>
        </div>

        {editing ? (
          <div className="mt-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {(['first_name', 'last_name', 'display_name', 'job_title', 'department', 'phone', 'timezone', 'locale'] as const).map(k => (
                <label key={k} className="min-w-0">
                  <span className="mb-1.5 block text-[11px] font-medium text-text-muted">{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  <input value={editData[k] || ''} onChange={e => setEditData(d => ({ ...d, [k]: e.target.value }))}
                    className="w-full min-w-0 rounded-xl bg-[#f5f8fc] px-3 py-2.5 text-sm text-text-primary outline-none ring-1 ring-border/45 transition focus:bg-white focus:ring-accent/30 dark:bg-slate-800/70 dark:focus:bg-slate-800" />
                </label>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <button onClick={saveProfile} disabled={saving} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isFirstTime ? 'Confirm & Activate' : 'Save Changes'}
              </button>
              <button onClick={() => setEditing(false)} className="min-h-11 rounded-xl px-4 py-2.5 text-xs font-medium text-text-muted transition hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 divide-y divide-border/50 sm:grid-cols-2 sm:gap-x-8 sm:divide-y-0">
            <Field label="First Name" value={profile?.first_name} />
            <Field label="Last Name" value={profile?.last_name} />
            <Field label="Display Name" value={profile?.display_name || user.display_name} />
            <Field label="Email" value={user.email} />
            <Field label="Phone" value={profile?.phone} />
            <Field label="Job Title" value={profile?.job_title} />
            <Field label="Department" value={profile?.department} />
            <Field label="Timezone" value={profile?.timezone} />
            <Field label="Locale" value={profile?.locale} />
          </div>
        )}
      </section>

      <section className="mt-3 w-full bg-white/95 px-5 py-6 shadow-[0_12px_32px_rgba(15,23,42,0.04)] dark:bg-slate-900/92 sm:mx-8 sm:mt-5 sm:w-[calc(100%-4rem)] sm:rounded-[1.75rem] sm:px-6 lg:mx-12 lg:w-[calc(100%-6rem)]">
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">Account</div>
        <h2 className="mt-1 text-lg font-semibold text-text-primary">Access identity</h2>
        <div className="mt-4 grid grid-cols-1 divide-y divide-border/50 sm:grid-cols-2 sm:gap-x-8 sm:divide-y-0">
          <Field label="Role" value={user.role} />
          <Field label="Status" value={user.status} />
          <div className="min-w-0 py-3.5 sm:col-span-2">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.13em] text-text-muted">Profile Completed</span>
            <span className={`block break-words text-[15px] leading-6 [overflow-wrap:anywhere] ${profile?.profile_completed_at ? 'text-emerald-600' : 'text-amber-600'}`}>
              {profile?.profile_completed_at ? '✓ ' + new Date(profile.profile_completed_at).toLocaleString() : '✗ Not yet confirmed'}
            </span>
          </div>
        </div>
      </section>
    </div>
  )
}
